import { html, nothing, unsafeCSS, type CSSResultGroup, type PropertyValues } from 'lit';
import { live } from 'lit/directives/live.js';
import { customElement, property, state } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import css from './lib-counter.css';
import buttonCss from '../button/lib-button.css';

const icons = {
  minus: new URL('../wago-icons-svg/minus.svg', import.meta.url).href,
  plus: new URL('../wago-icons-svg/plus.svg', import.meta.url).href,
  edit: new URL('../wago-icons-svg/edit-pen.svg', import.meta.url).href,
};

/** An integer counter with bounded steps and optional direct editing. */
@customElement('lib-counter')
export class LibCounter extends LibElement {
  protected override get hostFocusSelector(): string { return 'input'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(buttonCss), unsafeCSS(css)];
  /** Includes the editable text in native HTML forms. */
  static readonly formAssociated = true;
  private readonly internals = typeof this.attachInternals === 'function' ? this.attachInternals() : undefined;
  /** Native form field name. */
  @property() name = '';
  /** Identity of the input and its internal label target. */
  @property({ attribute: 'input-id' }) inputId = '';
  /** Autofill hint forwarded to the input. */
  @property() autocomplete = '';
  /** Focuses the enabled input after its first render. */
  @property({ type: Boolean }) autofocus = false;

  @property({ attribute: 'decrease-label' }) decreaseLabel = 'Decrease';
  @property({ attribute: 'increase-label' }) increaseLabel = 'Increase';
  @property({ attribute: 'counter-label' }) counterLabel = 'Counter';
  @property({ reflect: true }) value = '0';

  get #numericValue(): number { return Number(this.value); }
  @property({ type: Number }) step = 1;
  @property({ type: Number }) min?: number;
  @property({ type: Number }) max?: number;
  @property() headline = '';
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) readonly = false;
  @property({ type: Boolean }) error = false;
  @property() message = '';
  @property() surface = 'white';
  @property({ attribute: 'aria-label' }) override ariaLabel: string | null = null;
  @state() private draft = '0';
  @state() private formDisabled = false;
  private defaultValue = '0';
  private reflectingValue = false;
  private pendingValues: string[] = [];
  private revision = 0;

  static override get observedAttributes(): string[] { return [...super.observedAttributes, 'server-state']; }
  private get effectivelyDisabled(): boolean { return this.disabled || this.formDisabled; }

  override attributeChangedCallback(name: string, previous: string | null, value: string | null): void {
    if (name === 'server-state') {
      this.applyServerState(value);
      return;
    }
    if (name === 'value' && this.hasAttribute('server-state')) return;
    if (name === 'value' && !this.reflectingValue) {
      const pending = this.pendingValues.indexOf(value ?? '');
      if (pending >= 0) this.pendingValues.splice(0, pending + 1);
      else {
        this.pendingValues = [];
        this.defaultValue = value ?? '0';
      }
    }
    super.attributeChangedCallback(name, previous, value);
  }

  private applyServerState(value: string | null): void {
    if (value === null) return;
    const state = JSON.parse(value) as { value: string; revision: number };
    if (state.revision >= this.revision) {
      if (state.value !== this.value) this.defaultValue = state.value;
      this.value = state.value;
      this.draft = state.value;
      this.syncFormValue();
    }
  }

  protected override willUpdate(changed: PropertyValues<this>): void {
    if (!this.hasUpdated && !this.hasAttribute('value')) this.defaultValue = this.value;
    if (changed.has('value')) this.draft = this.value;
    this.syncFormValue();
  }

  protected override update(changed: PropertyValues<this>): void {
    // Lit reflection must not replace the reset baseline with a user edit.
    this.reflectingValue = true;
    try { super.update(changed); }
    finally { this.reflectingValue = false; }
  }

  protected override firstUpdated(): void {
    if (this.autofocus && !this.effectivelyDisabled)
      this.renderRoot.querySelector('input')?.focus();
  }

  /** Restores the last externally supplied value, not automatically reflected edits. */
  formResetCallback(): void {
    const changed = this.value !== this.defaultValue || this.draft !== this.defaultValue;
    this.revision++;
    this.pendingValues = [];
    this.value = this.defaultValue;
    this.draft = this.value;
    this.syncFormValue();
    if (changed) this.notifyChange();
  }

  override disconnectedCallback(): void {
    this.pendingValues = [];
    super.disconnectedCallback();
  }

  /** Applies disabled-fieldset state to the input and both stepper buttons. */
  formDisabledCallback(disabled: boolean): void {
    this.formDisabled = disabled;
    this.syncFormValue();
  }

  private syncFormValue(): void {
    if (typeof this.internals?.setFormValue === 'function')
      this.internals.setFormValue(this.effectivelyDisabled ? null : this.draft);
  }

  private input(event: Event): void {
    event.stopPropagation();
    if (this.effectivelyDisabled || this.readonly) return;
    this.revision++;
    this.draft = (event.target as HTMLInputElement).value;
    this.syncFormValue();
  }

  override render() {
    const disabled = this.effectivelyDisabled;
    const inputId = this.inputId || 'input';
    const invalid = this.error || !!this.message;
    const editStyle = `--icon: url("${icons.edit}")`;
    const classes = `lib-counter lib-counter-surface-${this.surface} ${disabled ? 'lib-counter-disabled' : ''} ${this.readonly ? 'lib-counter-readonly' : ''} ${invalid ? 'lib-counter-error' : ''}`;
    return html`<div class=${classes}>
      <div class="lib-counter-label-container" ?hidden=${!this.headline && !this.querySelector('[slot=help]')}>
        ${this.headline ? html`<label class="lib-counter-label" for=${inputId}>${this.headline}</label>` : nothing}
        <slot name="help"></slot>
      </div>
      <div class="lib-counter-container">
        ${this.#button(this.decreaseLabel, 'minus', this.#numericValue <= (this.min ?? -2147483648), -1)}
        <div class="lib-counter-field">
          <input id=${inputId} name=${this.name || nothing} autocomplete=${this.autocomplete || nothing}
            class="lib-counter-input" type="number" .value=${live(this.draft)}
            min=${this.min ?? nothing} max=${this.max ?? nothing} step=${Math.max(1, this.step)}
            ?readonly=${this.readonly} ?disabled=${disabled}
            aria-label=${this.ariaLabel || (this.headline ? nothing : this.counterLabel)}
            aria-invalid=${invalid ? 'true' : nothing} aria-describedby=${this.message ? 'message' : nothing}
            aria-valuenow=${this.value} aria-valuemin=${this.min ?? nothing} aria-valuemax=${this.max ?? nothing}
            @input=${this.input} @change=${this.#change} />
          ${this.readonly ? nothing : html`<span class="icon lib-counter-edit" aria-hidden="true" style=${editStyle}></span>`}
        </div>
        ${this.#button(this.increaseLabel, 'plus', this.#numericValue >= (this.max ?? 2147483647), 1)}
      </div>
      ${this.message ? html`<div id="message" class="lib-counter-message">${this.message}</div>` : nothing}
    </div>`;
  }

  #button(label: string, icon: 'minus' | 'plus', atBound: boolean, direction: number) {
    return html`<button type="button" class="lib-button lib-button-tertiary lib-counter-stepper lib-counter-${direction < 0 ? 'decrement' : 'increment'}"
      aria-label=${label} title=${label} ?disabled=${this.effectivelyDisabled || this.readonly || atBound}
      @click=${() => this.#setValue(this.#numericValue + direction * Math.max(1, this.step))}>
      <span class="icon" aria-hidden="true" style=${`--icon: url("${icons[icon]}")`}></span>
    </button>`;
  }

  #change(event: Event): void {
    event.stopPropagation();
    const input = event.target as HTMLInputElement;
    const value = Number(input.value);
    if (!this.effectivelyDisabled && !this.readonly && /^[+-]?\d+$/.test(input.value.trim()) && Number.isInteger(value)
      && value >= -2147483648 && value <= 2147483647) this.#setValue(value);
    input.value = String(this.value);
    this.draft = this.value;
    this.syncFormValue();
  }

  private notifyChange(): void {
    this.dispatchEvent(new CustomEvent('lib-counter-change', {
      detail: { value: this.value, revision: this.revision }, bubbles: true, composed: true,
    }));
    if (!this.hasAttribute('server-state'))
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  #setValue(value: number): void {
    if (this.effectivelyDisabled || this.readonly) return;
    const next = Math.min(Math.max(value, this.min ?? -2147483648), this.max ?? 2147483647);
    if (next === this.#numericValue) return;
    this.revision++;
    this.value = String(next);
    if (this.hasAttribute('value') && !this.hasAttribute('server-state')) this.pendingValues.push(this.value);
    this.draft = this.value;
    this.syncFormValue();
    this.notifyChange();
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-counter': LibCounter; }
}
